/**
 * StudyShowcase — Phase 9 Final / Polish Pass
 * "Você vai aprender" hero, concept cards, collapsible modules, equal-height
 * scrollable columns, progressive disclosure, premium spacing.
 */

import React from 'react';
import { usePresentationBreakpoint, AthenaTip, ActionChips, HeroPhotoPanel, MetricStrip }
  from '../presentation-system';
import { ProseText } from './ProseText';

// ── Palette ───────────────────────────────────────────────────────────────────
const P = {
  card:       '#0c1220',
  border:     '#1a2240',
  borderSoft: 'rgba(30,50,80,0.6)',
  accent:     '#3b82f6',
  accentSoft: 'rgba(59,130,246,0.12)',
  accentBdr:  'rgba(59,130,246,0.22)',
  accentL:    '#60a5fa',
  text:       '#e8eaf2',
  textSec:    'rgba(220,234,255,0.78)',
  textMut:    'rgba(150,170,220,0.6)',
  indigo:     '#818cf8',
  indigoBdr:  'rgba(99,102,241,0.3)',
  indigoSoft: 'rgba(99,102,241,0.12)',
  heroGrad:   'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
};

const _card = {
  background: P.card,
  border: `1px solid ${P.border}`,
  borderRadius: 14,
  padding: '14px 18px',
  boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
};

const _sLabel = {
  display: 'flex', alignItems: 'center', gap: 7,
  fontSize: 11, fontWeight: 800, color: P.textMut,
  textTransform: 'uppercase', letterSpacing: '0.09em',
};

const SCROLLBAR_CSS = `
  .ath-sc::-webkit-scrollbar { width: 3px; }
  .ath-sc::-webkit-scrollbar-track { background: transparent; }
  .ath-sc::-webkit-scrollbar-thumb { background: rgba(59,130,246,0.28); border-radius: 3px; }
  .ath-sc::-webkit-scrollbar-thumb:hover { background: rgba(59,130,246,0.5); }
`;

const MAX_LIST    = 8;
const ITEM_H      = 33;
const MAX_BULLETS = 8;

// ── Helpers ───────────────────────────────────────────────────────────────────
function deriveMeta(d) {
  const depth = d.depth_level || 'med';
  const level = { deep: 'Avançado', med: 'Intermediário', intro: 'Básico' }[depth] || 'Intermediário';
  const timeMap = { intro: '~15 min', med: '~30 min', deep: '~45 min' };
  const time = d.estimated_time || timeMap[depth] || '~30 min';
  const sections = d.sections || [];
  const moduleCount = d.section_count ?? sections.length;
  return { level, time, moduleCount };
}

function estimateSectionTime(totalTime, count) {
  const m = (totalTime || '').match(/\d+/);
  return `~${Math.max(3, Math.round((m ? parseInt(m[0]) : 30) / (count || 1)))} min`;
}

// ── Hero "Você vai aprender" section ─────────────────────────────────────────
function HeroLearnPreview({ sections, desc }) {
  const topics = (sections || []).slice(0, 4);
  if (topics.length >= 2) {
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 9.5, color: P.textMut, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 7 }}>
          Você vai aprender
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 10px' }}>
          {topics.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <div style={{ width: 4, height: 4, borderRadius: '50%', background: P.accentL, flexShrink: 0, opacity: 0.65 }} />
              <span style={{ fontSize: 12, color: P.textSec, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.4 }}>
                {s.title}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return desc ? (
    <div style={{ fontSize: 13, color: P.textSec, lineHeight: 1.55, marginTop: 8,
      display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
    }}>{desc}</div>
  ) : null;
}

// ── Hero Desktop ──────────────────────────────────────────────────────────────
function StudyHeroDesktop({ d }) {
  const { level, time, moduleCount } = deriveMeta(d);
  const title   = d.title || 'Estudo';
  const titleFs = title.length > 36 ? 22 : title.length > 22 ? 27 : 32;
  const METRICS = [
    { icon: '⏱', value: time,                                                label: 'Duração'  },
    { icon: '📋', value: moduleCount > 0 ? `${moduleCount} tópicos` : 'Estudo', label: 'Conteúdo' },
    { icon: '📊', value: level,                                               label: 'Nível'    },
    { icon: '🎯', value: 'Estudo',                                            label: 'Tipo'     },
  ];
  return (
    <div style={{ ..._card, padding: 0, overflow: 'hidden', display: 'flex', height: 260,
      background: P.heroGrad,
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <HeroPhotoPanel
        heroImage={d.hero_image} badge="📚 ESTUDO · AULA"
        accentColor={P.accent} accentFade={P.accentSoft} accentBdr={P.accentBdr}
        placeholder="📚" placeholderLabel="Estudo" title={title}
      />
      <div style={{ flex: 1, padding: '22px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
        <div>
          <div style={{ fontSize: titleFs, fontWeight: 800, color: P.text, lineHeight: 1.15, letterSpacing: '-0.025em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>{title}</div>
          <HeroLearnPreview sections={d.sections} desc={d.intro || d.subtitle} />
        </div>
        <MetricStrip metrics={METRICS} accentColor={P.accent} />
      </div>
    </div>
  );
}

// ── Hero Mobile ───────────────────────────────────────────────────────────────
function StudyHeroMobile({ d }) {
  const { level, time, moduleCount } = deriveMeta(d);
  const title   = d.title || 'Estudo';
  const METRICS = [
    { icon: '⏱', value: time,                                     label: 'Duração' },
    { icon: '📋', value: moduleCount > 0 ? `${moduleCount}` : '-', label: 'Tópicos' },
    { icon: '📊', value: level,                                    label: 'Nível'   },
    { icon: '🎯', value: 'Estudo',                                 label: 'Tipo'    },
  ];
  return (
    <div style={{ ..._card, padding: 0, overflow: 'hidden', background: P.heroGrad,
      boxShadow: '0 4px 40px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', minHeight: 202 }}>
        <div style={{ flex: 1, minWidth: 0, padding: '18px 10px 16px 16px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          <div style={{ display: 'inline-flex', alignSelf: 'flex-start', padding: '3px 10px', borderRadius: 20, marginBottom: 10,
            background: P.accentSoft, border: `1px solid ${P.accentBdr}`,
            fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', color: P.accentL, textTransform: 'uppercase',
          }}>📚 ESTUDO</div>
          <div style={{ fontSize: title.length > 30 ? 18 : 22, fontWeight: 800, color: P.text, lineHeight: 1.2, letterSpacing: '-0.025em' }}>
            {title}
          </div>
          {(d.intro || d.subtitle) && (
            <div style={{ fontSize: 12, color: P.textSec, marginTop: 6, lineHeight: 1.5,
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>{d.intro || d.subtitle}</div>
          )}
        </div>
        <HeroPhotoPanel
          heroImage={d.hero_image} isMobile
          accentColor={P.accent} accentFade={P.accentSoft} accentBdr={P.accentBdr}
          placeholder="📚" title={title}
        />
      </div>
      <div style={{ borderTop: `1px solid ${P.border}`, background: 'rgba(0,0,0,0.25)' }}>
        <MetricStrip metrics={METRICS} bordered accentColor={P.accent} />
      </div>
    </div>
  );
}

// ── Topics Column (desktop, scrollable) ───────────────────────────────────────
function TopicsColumn({ sections }) {
  const total      = sections.length;
  const needsScroll = total > MAX_LIST;
  return (
    <div style={{ ..._card, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexShrink: 0 }}>
        <div style={_sLabel}><span style={{ fontSize: 13 }}>📑</span><span>Tópicos Principais</span></div>
        <span style={{ fontSize: 10, color: P.textMut, background: P.borderSoft, borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>
          {total} no total
        </span>
      </div>
      <div className="ath-sc" style={{ display: 'flex', flexDirection: 'column', gap: 4,
        overflowY: needsScroll ? 'auto' : 'visible',
        maxHeight: needsScroll ? MAX_LIST * ITEM_H : 'none',
        paddingRight: needsScroll ? 4 : 0,
      }}>
        {sections.map((sec, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '3px 2px', flexShrink: 0 }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              background: i === 0 ? P.accent : 'rgba(59,130,246,0.72)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, color: '#fff',
              boxShadow: i === 0 ? `0 0 6px ${P.accent}45` : 'none',
            }}>{i + 1}</div>
            <span style={{ fontSize: 13, color: i === 0 ? P.text : P.textSec, lineHeight: 1.45, fontWeight: i === 0 ? 500 : 400 }}>
              {sec.title}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Concepts Column (desktop, scrollable) ─────────────────────────────────────
function ConceptsColumn({ concepts, objectives }) {
  const items      = concepts.length > 0 ? concepts : objectives;
  const total      = items.length;
  const needsScroll = total > MAX_LIST;
  return (
    <div style={{ ..._card, flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexShrink: 0 }}>
        <div style={_sLabel}><span style={{ fontSize: 13 }}>💡</span><span>Conceitos-Chave</span></div>
        <span style={{ fontSize: 10, color: P.textMut, background: P.borderSoft, borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>
          {total} no total
        </span>
      </div>
      <div className="ath-sc" style={{ display: 'flex', flexDirection: 'column', gap: 4,
        overflowY: needsScroll ? 'auto' : 'visible',
        maxHeight: needsScroll ? MAX_LIST * ITEM_H : 'none',
        paddingRight: needsScroll ? 4 : 0,
      }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '3px 2px', flexShrink: 0 }}>
            <div style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 2,
              background: P.indigoSoft, border: `1px solid ${P.indigoBdr}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: P.indigo, opacity: 0.85 }} />
            </div>
            <span style={{ fontSize: 13, color: P.textSec, lineHeight: 1.45 }}>{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Objective Card ────────────────────────────────────────────────────────────
function ObjectiveCard({ objectives }) {
  if (!objectives || objectives.length === 0) return null;
  return (
    <div style={{ ..._card }}>
      <div style={{ ..._sLabel, marginBottom: 8 }}><span style={{ fontSize: 13 }}>🎯</span><span>Objetivo do Estudo</span></div>
      {objectives.length === 1 ? (
        <p style={{ margin: 0, fontSize: 14, color: P.textSec, lineHeight: 1.65 }}>{objectives[0]}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {objectives.slice(0, 3).map((obj, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: P.accent, flexShrink: 0, marginTop: 8, opacity: 0.8 }} />
              <span style={{ fontSize: 13.5, color: P.textSec, lineHeight: 1.6 }}>{obj}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Concept mini-card (inside section content) ────────────────────────────────
function ConceptCard({ name, description }) {
  return (
    <div style={{
      background: `${P.accent}07`,
      border: `1px solid ${P.accentBdr}`,
      borderLeft: `2px solid ${P.accent}60`,
      borderRadius: 8, padding: '7px 12px',
    }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: P.text, lineHeight: 1.35, marginBottom: description ? 3 : 0 }}>
        {name}
      </div>
      {description && (
        <div style={{ fontSize: 12.5, color: P.textSec, lineHeight: 1.5 }}>{description}</div>
      )}
    </div>
  );
}

// ── Section Content ───────────────────────────────────────────────────────────
function SectionContent({ sec }) {
  const [showAll, setShowAll] = React.useState(false);
  const content = sec.content || '';

  if (sec.type === 'bullets' || sec.type === 'steps') {
    const allLines = content.split('\n').map(l => l.trim()).filter(Boolean);
    const visible  = showAll ? allLines : allLines.slice(0, MAX_BULLETS);
    const hasMore  = allLines.length > MAX_BULLETS;
    const extra    = allLines.length - MAX_BULLETS;

    return (
      <div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {visible.map((line, i) => {
            // Detect **Concept**: description pattern → concept card
            const conceptMatch = line.match(/^\*\*([^*]+)\*\*[:\s]+(.+)$/);
            if (conceptMatch && sec.type === 'bullets') {
              return <ConceptCard key={i} name={conceptMatch[1].trim()} description={conceptMatch[2].trim()} />;
            }
            const clean = line.replace(/^[\s\-*•\d.]+/, '').trim();
            if (!clean) return null;
            if (sec.type === 'steps') {
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(99,102,241,0.8)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0, marginTop: 2,
                  }}>{i + 1}</div>
                  <span style={{ fontSize: 13.5, color: P.textSec, lineHeight: 1.6 }}>{parseBold(clean)}</span>
                </div>
              );
            }
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: P.accentL, flexShrink: 0, marginTop: 7, opacity: 0.7 }} />
                <span style={{ fontSize: 13.5, color: P.textSec, lineHeight: 1.6 }}>{parseBold(clean)}</span>
              </div>
            );
          })}
        </div>
        {hasMore && !showAll && (
          <button
            onClick={() => setShowAll(true)}
            style={{
              marginTop: 10, padding: '6px 14px', borderRadius: 8,
              background: P.accentSoft, border: `1px solid ${P.accentBdr}`,
              color: P.accentL, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', outline: 'none', fontFamily: 'inherit',
              display: 'block',
            }}
          >
            + {extra} {extra === 1 ? 'item a mais' : 'itens a mais'}
          </button>
        )}
      </div>
    );
  }

  // Plain text
  return (
    <ProseText
      text={content}
      as="div"
      style={{ fontSize: 13.5, color: P.textSec, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
      boldStyle={{ color: P.text, fontWeight: 600 }}
    />
  );
}

function parseBold(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  if (parts.length === 1) return text;
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} style={{ color: P.text, fontWeight: 600 }}>{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

function isComparisonSection(sec) {
  const t = (sec.title || '').toLowerCase();
  const c = (sec.content || '').toLowerCase();
  return /\bvs\b|\b×\b|versus|comparaç|diferença entre|\bx\b/.test(t + ' ' + c);
}

function ComparisonGrid({ sec }) {
  const lines = (sec.content || '').split('\n').filter(l => l.trim());
  const [lhs, rhs] = (() => {
    const l = [], r = [];
    let side = 0;
    for (const line of lines) {
      const clean = line.replace(/^[\s\-*•]+/, '').trim();
      if (!clean) { side = 1; continue; }
      if (/^#{1,4}/.test(line) || /\bvs\b|\b×\b/.test(line)) continue;
      if (side === 0) l.push(clean); else r.push(clean);
    }
    return [l, r];
  })();
  const parts = sec.title.split(/\s+(?:vs|×|versus)\s+/i);
  return (
    <div style={{ ..._card }}>
      <div style={{ ..._sLabel, marginBottom: 12 }}><span style={{ fontSize: 13 }}>⚖️</span><span>{sec.title}</span></div>
      <div style={{ display: 'flex', gap: 10 }}>
        {[{label: parts[0] || 'A', items: lhs, col: '#3b82f6'}, {label: parts[1] || 'B', items: rhs, col: '#a78bfa'}].map((side, si) => (
          <div key={si} style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: side.col, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, padding: '4px 8px', background: `${side.col}12`, borderRadius: 6, border: `1px solid ${side.col}25` }}>
              {side.label}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {side.items.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: side.col, flexShrink: 0, marginTop: 8, opacity: 0.7 }}/>
                  <span style={{ fontSize: 12.5, color: P.textSec, lineHeight: 1.55 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Section Card (premium collapsible, first open by default) ─────────────────
function SectionCard({ sec, index, defaultExpanded = true, expandAll = false, timeEstimate }) {
  const [local, setLocal] = React.useState(defaultExpanded);
  const expanded = expandAll || local;

  const summary = React.useMemo(() => {
    const first = (sec.content || '')
      .replace(/\*\*/g, '')
      .split('\n')
      .map(l => l.replace(/^[\s\-*•\d.]+/, '').trim())
      .find(l => l.length > 10) || '';
    return first.length > 130 ? first.slice(0, 127) + '…' : first;
  }, [sec.content]);

  const isCmp = isComparisonSection(sec);
  if (isCmp && (sec.content || '').split('\n').filter(l => l.trim()).length > 3) {
    return <ComparisonGrid sec={sec} />;
  }

  return (
    <div style={{ ..._card, padding: 0, overflow: 'hidden',
      borderColor: expanded ? 'rgba(59,130,246,0.2)' : P.border,
      transition: 'border-color 0.2s ease',
    }}>
      <button
        onClick={() => !expandAll && setLocal(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '13px 16px',
          background: expanded ? 'rgba(59,130,246,0.03)' : 'transparent',
          border: 'none', cursor: expandAll ? 'default' : 'pointer',
          outline: 'none', fontFamily: 'inherit',
          transition: 'background 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0, flex: 1 }}>
          {/* Number badge — filled when expanded */}
          <div style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: expanded ? P.accent : 'rgba(59,130,246,0.12)',
            border: `1px solid ${expanded ? P.accent : 'rgba(59,130,246,0.2)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 800,
            color: expanded ? '#fff' : P.accentL,
            boxShadow: expanded ? `0 2px 8px ${P.accent}40` : 'none',
            transition: 'all 0.2s ease', flexShrink: 0,
          }}>{index + 1}</div>

          <div style={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: expanded ? 700 : 600, color: P.text, lineHeight: 1.3, transition: 'font-weight 0.15s' }}>
              {sec.title}
            </div>
            {!expanded && summary && (
              <div style={{ fontSize: 12, color: P.textMut, lineHeight: 1.4, marginTop: 3,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{summary}</div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, marginLeft: 12 }}>
          {timeEstimate && (
            <span style={{ fontSize: 11, color: P.textMut, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {timeEstimate}
            </span>
          )}
          {!expandAll && (
            <span style={{
              fontSize: 14, color: expanded ? P.accentL : P.textMut,
              transform: expanded ? 'rotate(-90deg)' : 'rotate(90deg)',
              transition: 'transform 0.18s ease, color 0.18s ease',
            }}>›</span>
          )}
        </div>
      </button>

      {expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${P.borderSoft}` }}>
          <div style={{ paddingTop: 14 }}>
            <SectionContent sec={sec} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Modules Section ───────────────────────────────────────────────────────────
function ModulesSection({ sections, totalTime }) {
  const [expandAll, setExpandAll] = React.useState(false);
  const timeEst = estimateSectionTime(totalTime, sections.length);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={_sLabel}><span style={{ fontSize: 13 }}>📚</span><span>Módulos do Estudo</span></div>
        <button
          onClick={() => setExpandAll(v => !v)}
          style={{ fontSize: 12, color: P.accentL, background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: '2px 0', outline: 'none' }}
        >
          {expandAll ? 'Recolher todos' : 'Expandir todos'}
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sections.map((sec, i) => (
          <SectionCard key={i} sec={sec} index={i} defaultExpanded={i === 0} expandAll={expandAll} timeEstimate={timeEst} />
        ))}
      </div>
    </div>
  );
}

// ── Mobile compact rows ───────────────────────────────────────────────────────
function MobileTopicsRow({ sections }) {
  const total   = sections.length;
  const visible = sections.slice(0, 6);
  const overflow = total - 6;
  return (
    <div style={{ ..._card }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={_sLabel}><span style={{ fontSize: 13 }}>📑</span><span>Tópicos Principais</span></div>
        <span style={{ fontSize: 10, color: P.textMut, background: P.borderSoft, borderRadius: 10, padding: '2px 8px' }}>{total} no total</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {visible.map((sec, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              background: i === 0 ? P.accent : 'rgba(59,130,246,0.72)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, color: '#fff',
              boxShadow: i === 0 ? `0 0 6px ${P.accent}45` : 'none',
            }}>{i + 1}</div>
            <span style={{ fontSize: 13, color: i === 0 ? P.text : P.textSec, lineHeight: 1.4, fontWeight: i === 0 ? 500 : 400 }}>
              {sec.title}
            </span>
          </div>
        ))}
        {overflow > 0 && (
          <div style={{ fontSize: 12, color: P.textMut, paddingLeft: 31 }}>+{overflow} tópicos</div>
        )}
      </div>
    </div>
  );
}

function MobileConceptsRow({ concepts, objectives }) {
  const items   = concepts.length > 0 ? concepts : objectives;
  const total   = items.length;
  const visible = items.slice(0, 6);
  const overflow = total - 6;
  return (
    <div style={{ ..._card }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={_sLabel}><span style={{ fontSize: 13 }}>💡</span><span>Conceitos-Chave</span></div>
        <span style={{ fontSize: 10, color: P.textMut, background: P.borderSoft, borderRadius: 10, padding: '2px 8px' }}>{total} no total</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {visible.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ color: P.indigo, fontSize: 13, flexShrink: 0, lineHeight: 1, fontWeight: 700 }}>✔</span>
            <span style={{ fontSize: 13, color: P.textSec, lineHeight: 1.4 }}>{item}</span>
          </div>
        ))}
        {overflow > 0 && (
          <div style={{ fontSize: 12, color: P.textMut, paddingLeft: 22 }}>+{overflow} conceitos</div>
        )}
      </div>
    </div>
  );
}

// ── Summary Sidebar ───────────────────────────────────────────────────────────
function SummaryCard({ d }) {
  const { level, time, moduleCount } = deriveMeta(d);
  const rows = [
    { icon: '📊', k: 'Nível',   v: level },
    { icon: '⏱', k: 'Duração', v: time },
    { icon: '📋', k: 'Tópicos', v: moduleCount > 0 ? `${moduleCount}` : 'Estudo' },
    { icon: '🎯', k: 'Tipo',    v: 'Estudo' },
  ];
  return (
    <div style={_card}>
      <div style={{ ..._sLabel, marginBottom: 12 }}><span style={{ fontSize: 13 }}>📋</span><span>Resumo Rápido</span></div>
      {rows.map(({ icon, k, v }, i) => (
        <div key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          padding: '6px 0', borderBottom: i < rows.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontSize: 12, opacity: 0.7 }}>{icon}</span>
            <span style={{ fontSize: 12, color: P.textMut }}>{k}</span>
          </div>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: P.text }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

// ── Actions Sidebar ───────────────────────────────────────────────────────────
const STUDY_ACTIONS = [
  { id: 'quiz',      icon: '❓', label: 'Fazer quiz'       },
  { id: 'flashcard', icon: '🃏', label: 'Flashcards'       },
  { id: 'summarize', icon: '📝', label: 'Resumir'          },
  { id: 'deepen',    icon: '🔬', label: 'Aprofundar'       },
  { id: 'examples',  icon: '💡', label: 'Exemplos práticos' },
];

function ActionsCard({ onAction }) {
  const [hov, setHov] = React.useState(null);
  return (
    <div style={_card}>
      <div style={{ ..._sLabel, marginBottom: 12 }}><span style={{ fontSize: 13 }}>⚡</span><span>Ações</span></div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {STUDY_ACTIONS.map((a) => (
          <button key={a.id} onClick={() => onAction && onAction(a.id)}
            onMouseEnter={() => setHov(a.id)} onMouseLeave={() => setHov(null)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%', padding: '8px 12px', borderRadius: 9,
              background: hov === a.id ? 'rgba(255,255,255,0.04)' : 'transparent',
              border: `1px solid ${hov === a.id ? 'rgba(255,255,255,0.1)' : P.border}`,
              color: P.textSec, fontSize: 13, cursor: 'pointer',
              transition: 'all 0.15s ease', outline: 'none', textAlign: 'left', fontFamily: 'inherit',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14 }}>{a.icon}</span><span>{a.label}</span>
            </div>
            <span style={{ fontSize: 11, color: P.textMut, opacity: hov === a.id ? 1 : 0.5, transition: 'opacity 0.15s' }}>›</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function StudyShowcase({ data, wp, onAction }) {
  const d        = wp || data;
  const isMobile = usePresentationBreakpoint();

  if (!d || d.layout !== 'study') return null;

  const sections   = d.sections     || [];
  const objectives = d.objectives   || [];
  const concepts   = d.key_concepts || [];
  const chips      = d.follow_up_chips || ['Aprofundar', 'Fazer quiz', 'Resumir', 'Exemplos práticos'];
  const { time }   = deriveMeta(d);

  const hasConcepts = concepts.length > 0 || objectives.length > 0;
  const hasSections = sections.length > 0;

  return (
    <>
      <style>{SCROLLBAR_CSS}</style>

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, width: '100%' }}>
          <StudyHeroMobile d={d} />
          {hasSections  && <MobileTopicsRow sections={sections} />}
          {hasConcepts  && <MobileConceptsRow concepts={concepts} objectives={objectives} />}
          {objectives.length > 0 && <ObjectiveCard objectives={objectives} />}
          {hasSections  && <ModulesSection sections={sections} totalTime={time} />}
          <AthenaTip tip={d.tip} />
          <ActionChips chips={chips} onAction={onAction} accentColor={P.accent} />
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', width: '100%' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <StudyHeroDesktop d={d} />

            {/* Equal-height Topics + Concepts */}
            {(hasSections || hasConcepts) && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
                {hasSections && (
                  <div style={{ flex: '0 0 45%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <TopicsColumn sections={sections} />
                  </div>
                )}
                {hasConcepts && (
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <ConceptsColumn concepts={concepts} objectives={objectives} />
                  </div>
                )}
              </div>
            )}

            {objectives.length > 0 && <ObjectiveCard objectives={objectives} />}
            {hasSections && <ModulesSection sections={sections} totalTime={time} />}
            <AthenaTip tip={d.tip} />
            <ActionChips chips={chips} onAction={onAction} accentColor={P.accent} />
          </div>

          {hasSections && (
            <div style={{ width: 210, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <SummaryCard d={d} />
              <ActionsCard onAction={onAction} />
            </div>
          )}
        </div>
      )}
    </>
  );
}
